/**
 * PR-BT-01 — Central Action Taxonomy
 *
 * Every action Smart Shikka can perform is classified into exactly one of four
 * categories. The category determines the default permission policy:
 *
 *   read_only          — allowed by default (chat, voice, read-only inspection)
 *   mutation           — default-off, requires owner-approved mutation unlock
 *   destructive        — default-off, requires multi-step owner approval + backup
 *   external_side_effect — default-off, requires owner approval + integration wiring
 *
 * The taxonomy is the single source of truth: the permission engine, approval
 * dispatcher, audit layer, and protected-scope registry all consult it.
 */
export const ACTION_CATEGORIES = [
    "read_only",
    "mutation",
    "destructive",
    "external_side_effect",
];
export const ACTION_RISK_LEVELS = [
    "safe",
    "low",
    "medium",
    "high",
    "critical",
];
export const FEATURE_SCOPES = [
    "chat",
    "voice",
    "provider",
    "file",
    "shell",
    "git",
    "browser",
    "desktop",
    "database",
    "messaging",
    "deploy",
    "payment",
    "cms",
    "knowledge",
    "security",
    "system",
];
/** Whether a category is default-on (allowed without explicit unlock). */
export function isDefaultOn(category) {
    return category === "read_only";
}
/** Whether a category requires owner approval before execution. */
export function requiresApproval(category) {
    return category !== "read_only";
}
/** Whether a category is permanently blocked (no approval can unlock). */
export function isPermanentlyBlocked(_category) {
    return false;
}
/** Check if an action on a protected scope is permanently blocked. */
export function isProtectedScopeActionBlocked(actionId, isProtectedScope) {
    if (!isProtectedScope)
        return false;
    const action = resolveAction(actionId);
    return action.category !== "read_only";
}
/** Human-readable description of each category. */
export const ACTION_CATEGORY_DESCRIPTIONS = {
    read_only: "Read-only actions: chat, voice, search, inspect, explain. Allowed by default.",
    mutation: "Mutation actions: create, update, write. Default-off; requires owner-approved mutation unlock.",
    destructive: "Destructive actions: delete, drop, wipe, force-push. Default-off; requires multi-step owner approval and backup.",
    external_side_effect: "External side-effect actions: send email/WhatsApp, payment, deploy, API calls. Default-off; requires owner approval and integration wiring.",
};
/** The central registry of known actions. */
const ACTION_REGISTRY = {};
/** Register an action in the taxonomy. */
export function registerAction(entry) {
    if (ACTION_REGISTRY[entry.actionId]) {
        throw new Error(`Action "${entry.actionId}" is already registered in the taxonomy.`);
    }
    ACTION_REGISTRY[entry.actionId] = Object.freeze({ ...entry });
}
/** Look up an action by its id. Returns undefined if not registered. */
export function getAction(actionId) {
    return ACTION_REGISTRY[actionId];
}
/** Classify an unknown action id into a fallback category. */
export function classifyUnknownAction(actionId) {
    return {
        actionId,
        category: "destructive",
        featureScope: "system",
        riskLevel: "critical",
        displayName: "Unknown Action",
        description: "Unclassified action — treated as destructive/critical by default.",
        defaultOn: false,
        approvalRequired: true,
    };
}
/** Resolve an action id to its taxonomy entry, falling back to destructive. */
export function resolveAction(actionId) {
    return getAction(actionId) ?? classifyUnknownAction(actionId);
}
/** List all registered actions. */
export function listRegisteredActions() {
    return Object.values(ACTION_REGISTRY);
}
/** List actions in a specific category. */
export function actionsByCategory(category) {
    return Object.values(ACTION_REGISTRY).filter((a) => a.category === category);
}
/** List actions in a specific feature scope. */
export function actionsByFeatureScope(scope) {
    return Object.values(ACTION_REGISTRY).filter((a) => a.featureScope === scope);
}
// ---------------------------------------------------------------------------
// Built-in action registrations
// ---------------------------------------------------------------------------
registerAction({
    actionId: "chat.send",
    category: "read_only",
    featureScope: "chat",
    riskLevel: "safe",
    displayName: "Chat Send",
    description: "Send a chat message to the assistant.",
    defaultOn: true,
    approvalRequired: false,
});
registerAction({
    actionId: "voice.transcribe",
    category: "read_only",
    featureScope: "voice",
    riskLevel: "safe",
    displayName: "Voice Transcribe",
    description: "Transcribe voice input to text.",
    defaultOn: true,
    approvalRequired: false,
});
registerAction({
    actionId: "voice.speak",
    category: "read_only",
    featureScope: "voice",
    riskLevel: "safe",
    displayName: "Voice Speak",
    description: "Speak text via TTS.",
    defaultOn: true,
    approvalRequired: false,
});
registerAction({
    actionId: "provider.health_check",
    category: "read_only",
    featureScope: "provider",
    riskLevel: "low",
    displayName: "Provider Health Check",
    description: "Read-only health check against a provider's safe endpoint.",
    defaultOn: true,
    approvalRequired: false,
});
registerAction({
    actionId: "provider.chat_completion",
    category: "external_side_effect",
    featureScope: "provider",
    riskLevel: "medium",
    displayName: "Provider Chat Completion",
    description: "Send a chat completion request to an LLM provider.",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "file.read",
    category: "read_only",
    featureScope: "file",
    riskLevel: "low",
    displayName: "File Read",
    description: "Read a file from an allowlisted workspace.",
    defaultOn: true,
    approvalRequired: false,
});
registerAction({
    actionId: "file.write",
    category: "mutation",
    featureScope: "file",
    riskLevel: "high",
    displayName: "File Write",
    description: "Write, create, or edit a file in an allowlisted workspace.",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "file.delete",
    category: "destructive",
    featureScope: "file",
    riskLevel: "critical",
    displayName: "File Delete",
    description: "Delete a file. Requires backup first.",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "shell.execute",
    category: "external_side_effect",
    featureScope: "shell",
    riskLevel: "high",
    displayName: "Shell Execute",
    description: "Execute a shell command.",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "git.commit",
    category: "mutation",
    featureScope: "git",
    riskLevel: "medium",
    displayName: "Git Commit",
    description: "Create a git commit on a branch.",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "git.push",
    category: "external_side_effect",
    featureScope: "git",
    riskLevel: "high",
    displayName: "Git Push",
    description: "Push commits to a remote.",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "git.force_push",
    category: "destructive",
    featureScope: "git",
    riskLevel: "critical",
    displayName: "Git Force Push",
    description: "Force-push, overwriting remote history.",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "browser.navigate",
    category: "read_only",
    featureScope: "browser",
    riskLevel: "low",
    displayName: "Browser Navigate",
    description: "Navigate to a URL (read-only).",
    defaultOn: true,
    approvalRequired: false,
});
registerAction({
    actionId: "browser.click",
    category: "mutation",
    featureScope: "browser",
    riskLevel: "medium",
    displayName: "Browser Click",
    description: "Click an element in the browser.",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "desktop.read",
    category: "read_only",
    featureScope: "desktop",
    riskLevel: "low",
    displayName: "Desktop Read",
    description: "Read desktop app settings or status.",
    defaultOn: true,
    approvalRequired: false,
});
registerAction({
    actionId: "desktop.act",
    category: "mutation",
    featureScope: "desktop",
    riskLevel: "high",
    displayName: "Desktop Act",
    description: "Perform a desktop action (launch app, click, type).",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "database.query",
    category: "read_only",
    featureScope: "database",
    riskLevel: "low",
    displayName: "Database Query",
    description: "Execute a read-only SQL query.",
    defaultOn: true,
    approvalRequired: false,
});
registerAction({
    actionId: "database.write",
    category: "mutation",
    featureScope: "database",
    riskLevel: "high",
    displayName: "Database Write",
    description: "Execute a SQL mutation (INSERT, UPDATE).",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "database.drop",
    category: "destructive",
    featureScope: "database",
    riskLevel: "critical",
    displayName: "Database Drop",
    description: "Drop a table or database. Destructive.",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "messaging.send",
    category: "external_side_effect",
    featureScope: "messaging",
    riskLevel: "high",
    displayName: "Messaging Send",
    description: "Send a message (email, WhatsApp, SMS).",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "messaging.bulk_send",
    category: "external_side_effect",
    featureScope: "messaging",
    riskLevel: "critical",
    displayName: "Bulk Messaging Send",
    description: "Send bulk messages. High-risk external side-effect.",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "deploy.execute",
    category: "external_side_effect",
    featureScope: "deploy",
    riskLevel: "critical",
    displayName: "Deploy Execute",
    description: "Deploy to a production environment.",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "payment.execute",
    category: "external_side_effect",
    featureScope: "payment",
    riskLevel: "critical",
    displayName: "Payment Execute",
    description: "Execute a payment or financial transaction.",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "cms.publish",
    category: "mutation",
    featureScope: "cms",
    riskLevel: "medium",
    displayName: "CMS Publish",
    description: "Publish content to a CMS.",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "knowledge.search",
    category: "read_only",
    featureScope: "knowledge",
    riskLevel: "safe",
    displayName: "Knowledge Search",
    description: "Search the knowledge base (read-only).",
    defaultOn: true,
    approvalRequired: false,
});
registerAction({
    actionId: "knowledge.write",
    category: "mutation",
    featureScope: "knowledge",
    riskLevel: "medium",
    displayName: "Knowledge Write",
    description: "Write to the knowledge base.",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "security.policy_edit",
    category: "destructive",
    featureScope: "security",
    riskLevel: "critical",
    displayName: "Security Policy Edit",
    description: "Edit security or permission policies. Permanently blocked for Smart Shikka self-modification.",
    defaultOn: false,
    approvalRequired: true,
});
registerAction({
    actionId: "system.config_edit",
    category: "mutation",
    featureScope: "system",
    riskLevel: "high",
    displayName: "System Config Edit",
    description: "Edit system configuration.",
    defaultOn: false,
    approvalRequired: true,
});
//# sourceMappingURL=actionTaxonomy.js.map